import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { marriages } from "@/lib/endpoints";
import { useApplicant } from "@/hooks/useApplicant";
import { useFormResponses } from "@/hooks/useFormResponses";
import { useNestedList } from "@/hooks/useNestedList";
import { getFieldErrors, getFormError } from "@/lib/validation-errors";
import { Field, Select, TextInput, YesNo } from "@/components/Field";
import type {
  CreateMarriageRequest,
  MarriageResponse,
} from "@/lib/api-types";

// form_responses keys for case-level current-spouse details. Names
// must match what the form mapping (n400_2025_01_20.go) reads.
const FR_KEYS = {
  marriedToUSC: "married_to_usc",
  spouseArmedForces: "current_spouse_armed_forces",
  spouseEmployer: "current_spouse_employer",
  spouseCitizenshipBasis: "current_spouse_citizenship_basis",
  spouseNaturalizationDate: "current_spouse_naturalization_date",
  spouseTimesMarried: "current_spouse_times_married",
} as const;

/**
 * Step 5 — Marital history (Part 7 of N-400).
 * Current spouse plus all previous marriages. The applicant's marital_status
 * (set in Step 1) drives whether this section is required.
 */
export function StepMarriage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { applicant, isLoading: applicantLoading } = useApplicant(id);
  const list = useNestedList<MarriageResponse, CreateMarriageRequest>(
    "marriages",
    marriages,
    id,
    applicant?.id,
  );

  const [editing, setEditing] = useState<MarriageResponse | "new" | null>(null);

  // Case-level current-spouse details (form_responses).
  const fr = useFormResponses(id);
  const [validationErr, setValidationErr] = useState<string | null>(null);
  const [marriedToUSC, setMarriedToUSC] = useState<boolean | null>(null);
  const [spouseArmedForces, setSpouseArmedForces] = useState<boolean | null>(null);
  const [spouseEmployer, setSpouseEmployer] = useState("");
  const [spouseCitizenshipBasis, setSpouseCitizenshipBasis] = useState("");
  const [spouseNaturalizationDate, setSpouseNaturalizationDate] = useState("");
  const [spouseTimesMarried, setSpouseTimesMarried] = useState("");

  useEffect(() => {
    if (fr.isLoading) return;
    const mtu = fr.get<boolean>(FR_KEYS.marriedToUSC);
    setMarriedToUSC(typeof mtu === "boolean" ? mtu : null);
    const saf = fr.get<boolean>(FR_KEYS.spouseArmedForces);
    setSpouseArmedForces(typeof saf === "boolean" ? saf : null);
    setSpouseEmployer(fr.getString(FR_KEYS.spouseEmployer));
    setSpouseCitizenshipBasis(fr.getString(FR_KEYS.spouseCitizenshipBasis));
    setSpouseNaturalizationDate(fr.getString(FR_KEYS.spouseNaturalizationDate));
    setSpouseTimesMarried(fr.getString(FR_KEYS.spouseTimesMarried));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fr.isLoading, ...Object.values(FR_KEYS).map((k) => fr.get(k))]);

  if (applicantLoading || list.isLoading) {
    return <div className="text-slate-500">Loading…</div>;
  }

  const isMarried = applicant?.marital_status === "married";
  const wasMarried = !!applicant?.marital_status && applicant.marital_status !== "single";

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Marital history</h1>
        <p className="text-sm text-slate-500 mt-1">
          {isMarried
            ? "Add your current spouse, plus any previous marriages."
            : wasMarried
              ? "Add each marriage you've had."
              : "Skip this step if you've never been married."}
        </p>
      </header>

      <ul className="space-y-3">
        {list.items.map((m) => (
          <li
            key={m.id}
            className="bg-white rounded-xl border border-slate-200 p-4 flex items-start justify-between"
          >
            <div className="text-sm">
              <div className="font-medium">{m.spouse_full_name}</div>
              <div className="text-slate-500">
                {m.start_date}
                {m.end_date ? ` → ${m.end_date} (${m.end_reason})` : " · current"}
              </div>
              <div className="text-xs text-slate-400 mt-1">
                {[m.marriage_location_city, m.marriage_location_country].filter(Boolean).join(", ")}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setEditing(m)}
                className="text-sm text-slate-600 hover:text-slate-900"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => {
                  if (confirm("Delete this marriage?")) list.remove.mutate(m.id);
                }}
                className="text-sm text-red-600 hover:text-red-700"
              >
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>

      {!editing && (
        <button
          type="button"
          onClick={() => setEditing("new")}
          className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          + Add marriage
        </button>
      )}

      {editing && (
        <MarriageForm
          initial={editing === "new" ? null : editing}
          onCancel={() => setEditing(null)}
          onSubmit={async (req) => {
            if (editing === "new") await list.create.mutateAsync(req);
            else await list.update.mutateAsync({ id: editing.id, req });
            setEditing(null);
          }}
          saving={list.create.isPending || list.update.isPending}
          error={editing === "new" ? list.create.error : list.update.error}
        />
      )}

      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setValidationErr(null);
          if (isMarried) {
            if (marriedToUSC === null) {
              setValidationErr("Please answer whether your spouse is a U.S. citizen.");
              return;
            }
            if (spouseArmedForces === null) {
              setValidationErr("Please answer whether your spouse serves in the U.S. Armed Forces.");
              return;
            }
          }
          const upserts: Promise<unknown>[] = [];
          const upsertStr = (key: string, val: string) => {
            if (val) upserts.push(fr.upsert.mutateAsync({ fieldKey: key, value: val }));
          };
          const upsertBool = (key: string, val: boolean | null) => {
            if (val !== null) upserts.push(fr.upsert.mutateAsync({ fieldKey: key, value: val }));
          };
          if (isMarried) {
            upsertBool(FR_KEYS.marriedToUSC, marriedToUSC);
            upsertBool(FR_KEYS.spouseArmedForces, spouseArmedForces);
            upsertStr(FR_KEYS.spouseEmployer, spouseEmployer);
            upsertStr(FR_KEYS.spouseTimesMarried, spouseTimesMarried);
            if (marriedToUSC === true) {
              upsertStr(FR_KEYS.spouseCitizenshipBasis, spouseCitizenshipBasis);
              if (spouseCitizenshipBasis === "other") {
                upsertStr(FR_KEYS.spouseNaturalizationDate, spouseNaturalizationDate);
              }
            }
          }
          await Promise.all(upserts);
          navigate(`/cases/${id}/wizard/children`);
        }}
        className="space-y-6"
      >
      {isMarried && (
        <fieldset className="bg-white rounded-xl border border-slate-200 p-5 space-y-5">
          <legend className="font-medium text-slate-900 px-1">
            Current spouse details
          </legend>
          <p className="text-sm text-slate-500">
            A few questions about your current spouse that go on Part 5
            of the N-400 alongside the marriage row above.
          </p>

          <div>
            <p className="text-sm font-medium text-slate-700 mb-2">
              Is your current spouse a U.S. citizen?
            </p>
            <YesNo value={marriedToUSC} onChange={setMarriedToUSC} />
          </div>

          {marriedToUSC === true && (
            <Field
              label="How did your spouse become a U.S. citizen?"
              required
            >
              <Select
                value={spouseCitizenshipBasis}
                onChange={(e) => setSpouseCitizenshipBasis(e.target.value)}
              >
                <option value="">Select…</option>
                <option value="by_birth">By birth in the U.S.</option>
                <option value="other">Naturalized or other (provide date)</option>
              </Select>
            </Field>
          )}

          {marriedToUSC === true && spouseCitizenshipBasis === "other" && (
            <Field
              label="Date your spouse became a U.S. citizen"
              hint="From their certificate of naturalization, if available."
              required
            >
              <TextInput
                type="date"
                value={spouseNaturalizationDate}
                onChange={(e) => setSpouseNaturalizationDate(e.target.value)}
              />
            </Field>
          )}

          <div>
            <p className="text-sm font-medium text-slate-700 mb-2">
              Is your spouse currently a member of the U.S. Armed Forces?
            </p>
            <YesNo value={spouseArmedForces} onChange={setSpouseArmedForces} />
          </div>

          <Field
            label="Spouse's current employer or company"
            hint="Leave blank if not employed."
          >
            <TextInput
              value={spouseEmployer}
              onChange={(e) => setSpouseEmployer(e.target.value)}
            />
          </Field>

          <Field
            label="How many times has your spouse been married?"
            hint="Including the current marriage."
          >
            <TextInput
              type="number"
              min={1}
              value={spouseTimesMarried}
              onChange={(e) => setSpouseTimesMarried(e.target.value)}
            />
          </Field>
        </fieldset>
      )}

      {validationErr && (
        <p className="text-sm text-red-600">{validationErr}</p>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-slate-200">
        <Link to={`/cases/${id}/wizard/travel`} className="text-sm text-slate-600 hover:text-slate-900">
          ← Travel
        </Link>
        <button
          type="submit"
          disabled={fr.upsert.isPending}
          className="rounded-xl bg-slate-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-slate-800 disabled:bg-slate-300"
        >
          {fr.upsert.isPending ? "Saving…" : "Save & continue"}
        </button>
      </div>
      </form>
    </div>
  );
}

function MarriageForm({
  initial,
  onCancel,
  onSubmit,
  saving,
  error,
}: {
  initial: MarriageResponse | null;
  onCancel: () => void;
  onSubmit: (req: CreateMarriageRequest) => Promise<void>;
  saving: boolean;
  error?: unknown;
}) {
  const [form, setForm] = useState<CreateMarriageRequest>({
    spouse_full_name: initial?.spouse_full_name ?? "",
    spouse_date_of_birth: initial?.spouse_date_of_birth ?? "",
    spouse_country_of_birth: initial?.spouse_country_of_birth ?? "",
    spouse_current_citizenship: initial?.spouse_current_citizenship ?? "",
    start_date: initial?.start_date ?? "",
    marriage_location_city: initial?.marriage_location_city ?? "",
    marriage_location_country: initial?.marriage_location_country ?? "",
    end_date: initial?.end_date ?? "",
    end_reason: initial?.end_reason ?? "",
  });

  const set = <K extends keyof CreateMarriageRequest>(
    k: K,
    v: CreateMarriageRequest[K],
  ) => setForm((s) => ({ ...s, [k]: v }));

  const fieldErrors = getFieldErrors(error);
  const formError = getFormError(error);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const clean: CreateMarriageRequest = { ...form };
        (Object.keys(clean) as (keyof CreateMarriageRequest)[]).forEach((k) => {
          if (clean[k] === "") (clean as Record<string, unknown>)[k] = undefined;
        });
        void onSubmit(clean);
      }}
      className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4"
    >
      <h3 className="font-semibold">{initial ? "Edit" : "Add marriage"}</h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field
          label="Spouse's full name"
          required
          className="sm:col-span-2"
          error={fieldErrors?.["spouse_full_name"]?.message}
        >
          <TextInput
            value={form.spouse_full_name ?? ""}
            onChange={(e) => set("spouse_full_name", e.target.value)}
          />
        </Field>
        <Field
          label="Spouse's date of birth"
          error={fieldErrors?.["spouse_date_of_birth"]?.message}
        >
          <TextInput
            type="date"
            value={form.spouse_date_of_birth ?? ""}
            onChange={(e) => set("spouse_date_of_birth", e.target.value)}
          />
        </Field>
        <Field
          label="Spouse's country of birth"
          error={fieldErrors?.["spouse_country_of_birth"]?.message}
        >
          <TextInput
            value={form.spouse_country_of_birth ?? ""}
            onChange={(e) => set("spouse_country_of_birth", e.target.value)}
          />
        </Field>
        <Field
          label="Spouse's current citizenship"
          className="sm:col-span-2"
          error={fieldErrors?.["spouse_current_citizenship"]?.message}
        >
          <TextInput
            value={form.spouse_current_citizenship ?? ""}
            onChange={(e) => set("spouse_current_citizenship", e.target.value)}
          />
        </Field>
        <Field
          label="Marriage date"
          required
          error={fieldErrors?.["start_date"]?.message}
        >
          <TextInput
            type="date"
            value={form.start_date ?? ""}
            onChange={(e) => set("start_date", e.target.value)}
          />
        </Field>
        <Field
          label="Marriage city"
          error={fieldErrors?.["marriage_location_city"]?.message}
        >
          <TextInput
            value={form.marriage_location_city ?? ""}
            onChange={(e) => set("marriage_location_city", e.target.value)}
          />
        </Field>
        <Field
          label="Marriage country"
          error={fieldErrors?.["marriage_location_country"]?.message}
        >
          <TextInput
            value={form.marriage_location_country ?? ""}
            onChange={(e) => set("marriage_location_country", e.target.value)}
          />
        </Field>
        <Field
          label="End date"
          hint="Leave blank if still married"
          error={fieldErrors?.["end_date"]?.message}
        >
          <TextInput
            type="date"
            value={form.end_date ?? ""}
            onChange={(e) => set("end_date", e.target.value)}
          />
        </Field>
        <Field
          label="End reason"
          className="sm:col-span-2"
          error={fieldErrors?.["end_reason"]?.message}
        >
          <Select
            value={form.end_reason ?? ""}
            onChange={(e) => set("end_reason", e.target.value)}
          >
            <option value="">— still married —</option>
            <option value="divorce">Divorce</option>
            <option value="annulment">Annulment</option>
            <option value="death">Spouse died</option>
            {/* "separation" removed — backend doesn't accept it; legal
                separation is a current marital status, not an end of
                marriage. */}
          </Select>
        </Field>
      </div>

      {formError && (
        <p className="text-sm text-red-600">Could not save: {formError}</p>
      )}
      {fieldErrors && (
        <p className="text-xs text-red-600">
          Some fields above need attention before continuing.
        </p>
      )}
      <div className="flex items-center justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="text-sm text-slate-600 hover:text-slate-900">
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="rounded-xl bg-slate-900 text-white px-4 py-2 text-sm font-medium hover:bg-slate-800 disabled:bg-slate-300"
        >
          {saving ? "Saving…" : initial ? "Save changes" : "Add"}
        </button>
      </div>
    </form>
  );
}
